"use client";

import { Mail, ArrowLeft, FileText } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { Logo } from "@/components/brand/logo";
import { cn } from "@/lib/utils";

const NAV = [
  { href: "/privacy" as const, label: "Privacy" },
  { href: "/terms" as const, label: "Terms" },
  { href: "/data-deletion" as const, label: "Data deletion" },
];

export type LegalSection = {
  id: string;
  label: string;
};

export function LegalShell({
  title,
  updated,
  summary,
  sections = [],
  children,
}: {
  title: string;
  updated: string;
  summary?: string;
  sections?: LegalSection[];
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.9]"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 15% -10%, color-mix(in oklch, var(--brand-cyan) 18%, transparent), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 0%, color-mix(in oklch, var(--brand-500) 16%, transparent), transparent 50%), linear-gradient(180deg, color-mix(in oklch, var(--brand-navy) 4%, transparent), transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.35] dark:opacity-[0.2]"
          style={{
            backgroundImage:
              "linear-gradient(color-mix(in oklch, var(--brand-navy) 8%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklch, var(--brand-navy) 8%, transparent) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "linear-gradient(180deg, black, transparent 85%)",
          }}
        />
      </div>

      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3.5 sm:px-8">
          <Link href="/login" className="inline-flex items-center gap-2">
            <Logo className="h-9" />
          </Link>
          <nav className="flex items-center gap-1 rounded-full border border-border/70 bg-background/70 p-1 shadow-sm shadow-brand-900/5">
            {NAV.map((item) => {
              const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "bg-brand-button-gradient text-white shadow-sm shadow-brand-900/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <div className="relative mx-auto grid max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-14 lg:py-14">
        {sections.length > 0 ? (
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-3">
              <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                On this page
              </p>
              <nav className="space-y-1 border-s border-border/80 ps-3">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block py-1 text-sm text-muted-foreground transition-colors hover:text-brand-600 dark:hover:text-brand-300"
                  >
                    {section.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        ) : (
          <div className="hidden lg:block" />
        )}

        <main className="min-w-0">
          <div className="mb-8 space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-500/15 bg-brand-50/80 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-950/50 dark:text-brand-300">
              <FileText className="size-3.5" />
              Legal
            </div>
            <div className="space-y-3">
              <h1 className="font-heading text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
                {title}
              </h1>
              <p className="text-sm text-muted-foreground">Last updated: {updated}</p>
              {summary ? (
                <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-[17px]">
                  {summary}
                </p>
              ) : null}
            </div>
          </div>

          {sections.length > 0 ? (
            <details className="mb-8 rounded-2xl border border-border/80 bg-background/70 p-4 lg:hidden">
              <summary className="cursor-pointer text-sm font-medium">On this page</summary>
              <nav className="mt-3 grid gap-1.5 ps-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    {section.label}
                  </a>
                ))}
              </nav>
            </details>
          ) : null}

          <article
            className={cn(
              "legal-article max-w-none text-[15.5px] leading-7 text-foreground/90",
              "[&_h2]:mt-10 [&_h2]:scroll-mt-28 [&_h2]:border-t [&_h2]:border-border/70 [&_h2]:pt-8",
              "[&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground",
              "[&_h2:first-of-type]:mt-0 [&_h2:first-of-type]:border-t-0 [&_h2:first-of-type]:pt-0",
              "[&_h3]:mt-6 [&_h3]:scroll-mt-28 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground",
              "[&_p]:mt-3 [&_p]:text-pretty",
              "[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:ps-5",
              "[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2.5 [&_ol]:ps-5",
              "[&_li]:marker:text-brand-500",
              "[&_strong]:font-semibold [&_strong]:text-foreground",
              "[&_a]:font-medium [&_a]:text-brand-600 [&_a]:underline-offset-4 hover:[&_a]:underline dark:[&_a]:text-brand-300"
            )}
          >
            {children}
          </article>

          <div className="mt-12 overflow-hidden rounded-2xl border border-border/80 bg-gradient-to-br from-brand-50/80 via-background to-background p-6 shadow-sm shadow-brand-900/5 dark:from-brand-950/40">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Questions about this policy?</p>
                <p className="text-sm text-muted-foreground">
                  Email our team and we&apos;ll get back to you.
                </p>
              </div>
              <a
                href="mailto:contact@snopdesk.com"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-button-gradient px-4 py-2.5 text-sm font-medium text-white shadow-md shadow-brand-900/15 transition hover:brightness-105"
              >
                <Mail className="size-4" />
                contact@snopdesk.com
              </a>
            </div>
          </div>

          <footer className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-6 text-sm text-muted-foreground">
            <Link
              href="/login"
              className="inline-flex items-center gap-1.5 font-medium text-foreground/80 transition hover:text-brand-600 dark:hover:text-brand-300"
            >
              <ArrowLeft className="size-3.5" />
              Back to login
            </Link>
            <p>© {new Date().getFullYear()} SnopDesk</p>
          </footer>
        </main>
      </div>
    </div>
  );
}
